import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, addMonths, startOfMonth } from "date-fns";
import {
  Home, CheckCircle, Clock, XCircle, TrendingUp, Calendar,
  ChevronRight, PoundSterling, Target, ArrowRight, GripVertical,
  Plus, X, BarChart3, List, Briefcase, Link2, FileText
} from "lucide-react";

const STATUS_CONFIG = {
  active: { label: "SSTC", color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-50" },
  exchanged: { label: "Exchanged", color: "bg-purple-500", textColor: "text-purple-700", bgLight: "bg-purple-50" },
  completed: { label: "Completed", color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-50" },
  fallen_through: { label: "Fallen Through", color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-50" }
};

export default function SalesProgressorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [activeTab, setActiveTab] = useState("pipeline");
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showFeeListModal, setShowFeeListModal] = useState(false);
  const [feeListStatus, setFeeListStatus] = useState(null);
  const [solicitorLeads, setSolicitorLeads] = useState({ total_leads: 0, qualifying_firms: [] });
  
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Generate next 6 months
  const months = Array.from({ length: 6 }, (_, i) => {
    const date = addMonths(startOfMonth(new Date()), i);
    return {
      key: format(date, "yyyy-MM"),
      label: format(date, "MMMM yyyy"),
      short: format(date, "MMM")
    };
  });

  useEffect(() => {
    fetchCases();
    fetchPredictions();
    fetchSolicitorLeads();
  }, []);

  const fetchCases = async () => {
    try {
      const res = await fetch(`${API}/sales/cases?include_all=true`, { headers });
      const data = await res.json();
      setCases(data.cases || []);
    } catch (error) {
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${API}/sales/predictions`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPredictions(data.predictions || {});
      }
    } catch (error) {
      console.error("Failed to load predictions");
    }
  };

  const fetchSolicitorLeads = async () => {
    try {
      const res = await fetch(`${API}/sales/solicitor-leads`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSolicitorLeads(data);
      }
    } catch (error) {
      console.error("Failed to load solicitor leads");
    }
  };

  const savePrediction = async (caseId, month) => {
    try {
      await fetch(`${API}/sales/predictions`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ case_id: caseId, predicted_month: month })
      });
      
      setPredictions(prev => ({ ...prev, [caseId]: month }));
      toast.success("Prediction saved");
      setShowPredictionModal(false);
    } catch (error) {
      toast.error("Failed to save prediction");
    }
  };

  const removePrediction = async (caseId) => {
    try {
      await fetch(`${API}/sales/predictions/${caseId}`, {
        method: "DELETE",
        headers
      });
      
      setPredictions(prev => {
        const updated = { ...prev };
        delete updated[caseId];
        return updated;
      });
      toast.success("Prediction removed");
    } catch (error) {
      toast.error("Failed to remove prediction");
    }
  };

  const updateCaseStatus = async (caseId, newStatus) => {
    try {
      await fetch(`${API}/sales/cases/${caseId}/status`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      
      setCases(prev => prev.map(c => 
        c.case_id === caseId ? { ...c, status: newStatus } : c
      ));
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Group cases by status
  const casesByStatus = {
    active: cases.filter(c => c.status === "active"),
    exchanged: cases.filter(c => c.status === "exchanged"),
    completed: cases.filter(c => c.status === "completed"),
    fallen_through: cases.filter(c => c.status === "fallen_through")
  };

  // Calculate monthly predictions
  const monthlyPredictions = months.map(month => {
    const monthCases = cases.filter(c => predictions[c.case_id] === month.key);
    const totalFees = monthCases.reduce((sum, c) => sum + (c.agreed_fee || 0), 0);
    return { ...month, cases: monthCases, totalFees };
  });

  // Stats
  const stats = {
    sstc: casesByStatus.active.length,
    sstcFees: casesByStatus.active.reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    exchanged: casesByStatus.exchanged.length,
    exchangedFees: casesByStatus.exchanged.reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    completed: casesByStatus.completed.length,
    completedFees: casesByStatus.completed.reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    fallenThrough: casesByStatus.fallen_through.length
  };

  const CaseCard = ({ caseData, showPrediction = false, draggable = false }) => (
    <div 
      className={`p-3 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow ${draggable ? 'cursor-grab' : ''}`}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("caseId", caseData.case_id);
        e.dataTransfer.setData("caseFee", caseData.agreed_fee || 0);
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{caseData.address_line1}</p>
          <p className="text-xs text-slate-500">{caseData.town}</p>
          {caseData.agreed_fee > 0 && (
            <p className="text-sm font-semibold text-emerald-600 mt-1">
              £{caseData.agreed_fee?.toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={`${STATUS_CONFIG[caseData.status]?.bgLight} ${STATUS_CONFIG[caseData.status]?.textColor} text-xs`}>
            {STATUS_CONFIG[caseData.status]?.label || caseData.status}
          </Badge>
          {showPrediction && predictions[caseData.case_id] && (
            <Badge variant="outline" className="text-xs">
              {months.find(m => m.key === predictions[caseData.case_id])?.short || predictions[caseData.case_id]}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t">
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-7 text-xs flex-1"
          onClick={() => navigate(`/sales/case/${caseData.case_id}`)}
        >
          View
        </Button>
        {caseData.status === "active" && (
          <>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 text-xs"
              onClick={() => { setSelectedCase(caseData); setShowPredictionModal(true); }}
            >
              <Target className="h-3 w-3 mr-1" />
              Predict
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 text-xs text-purple-600"
              onClick={() => updateCaseStatus(caseData.case_id, "exchanged")}
            >
              Exchange
            </Button>
          </>
        )}
        {caseData.status === "exchanged" && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 text-xs text-green-600"
            onClick={() => updateCaseStatus(caseData.case_id, "completed")}
          >
            Complete
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sales Progression</h1>
              <p className="text-sm text-slate-500">Track SSTC cases through to completion</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/sales/chain")}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Notes / Chain View
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/sales/solicitors")}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Solicitor Firms
              </Button>
              <Button variant="outline" onClick={() => navigate("/home")}>
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Overview - Clickable fees */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card 
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setFeeListStatus("active"); setShowFeeListModal(true); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">SSTC</p>
                  <p className="text-3xl font-bold">{stats.sstc}</p>
                  <p className="text-blue-100 text-sm underline hover:text-white">
                    £{stats.sstcFees.toLocaleString()}
                  </p>
                </div>
                <Clock className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setFeeListStatus("exchanged"); setShowFeeListModal(true); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Exchanged</p>
                  <p className="text-3xl font-bold">{stats.exchanged}</p>
                  <p className="text-purple-100 text-sm underline hover:text-white">
                    £{stats.exchangedFees.toLocaleString()}
                  </p>
                </div>
                <CheckCircle className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setFeeListStatus("completed"); setShowFeeListModal(true); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Completed</p>
                  <p className="text-3xl font-bold">{stats.completed}</p>
                  <p className="text-green-100 text-sm underline hover:text-white">
                    £{stats.completedFees.toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setFeeListStatus("fallen_through"); setShowFeeListModal(true); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Fallen Through</p>
                  <p className="text-3xl font-bold">{stats.fallenThrough}</p>
                </div>
                <XCircle className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Solicitor Leads This Month */}
        <div className="mb-6">
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <Briefcase className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Solicitor Leads This Month</p>
                    <p className="text-3xl font-bold">{solicitorLeads.total_leads || 0}</p>
                    <p className="text-indigo-200 text-xs">
                      {solicitorLeads.qualifying_firms?.slice(0, 3).join(' • ') || 'No qualifying firms yet'}
                    </p>
                  </div>
                </div>
                <div className="text-right text-indigo-200 text-sm">
                  <p>£25 each</p>
                  <p className="text-white font-semibold">= £{(solicitorLeads.total_leads || 0) * 25}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pipeline" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Pipeline
            </TabsTrigger>
            <TabsTrigger value="view-all" className="gap-2" data-testid="view-all-tab">
              <List className="h-4 w-4" />
              View All
            </TabsTrigger>
            <TabsTrigger value="predictions" className="gap-2">
              <Target className="h-4 w-4" />
              Monthly Predictions
            </TabsTrigger>
          </TabsList>

          {/* Pipeline View */}
          <TabsContent value="pipeline">
            <div className="grid lg:grid-cols-4 gap-4">
              {/* SSTC Column */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG.active.color}`} />
                    SSTC ({casesByStatus.active.length})
                  </CardTitle>
                  <p className="text-sm text-emerald-600 font-semibold">
                    £{stats.sstcFees.toLocaleString()}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {casesByStatus.active.map(c => (
                    <CaseCard key={c.case_id} caseData={c} showPrediction draggable />
                  ))}
                  {casesByStatus.active.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No SSTC cases</p>
                  )}
                </CardContent>
              </Card>

              {/* Exchanged Column */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG.exchanged.color}`} />
                    Exchanged ({casesByStatus.exchanged.length})
                  </CardTitle>
                  <p className="text-sm text-purple-600 font-semibold">
                    £{stats.exchangedFees.toLocaleString()}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {casesByStatus.exchanged.map(c => (
                    <CaseCard key={c.case_id} caseData={c} />
                  ))}
                  {casesByStatus.exchanged.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No exchanged cases</p>
                  )}
                </CardContent>
              </Card>

              {/* Completed Column */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG.completed.color}`} />
                    Completed ({casesByStatus.completed.length})
                  </CardTitle>
                  <p className="text-sm text-green-600 font-semibold">
                    £{stats.completedFees.toLocaleString()}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {casesByStatus.completed.map(c => (
                    <CaseCard key={c.case_id} caseData={c} />
                  ))}
                  {casesByStatus.completed.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No completions yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Fallen Through Column */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG.fallen_through.color}`} />
                    Fallen Through ({casesByStatus.fallen_through.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {casesByStatus.fallen_through.map(c => (
                    <CaseCard key={c.case_id} caseData={c} />
                  ))}
                  {casesByStatus.fallen_through.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">None fallen through</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* View All - Simple List View */}
          <TabsContent value="view-all">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <List className="h-5 w-5" />
                  All Properties Overview
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Click any property to view its sales progression milestones
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Combined list of SSTC, Exchanged, and Completed */}
                  {[...casesByStatus.active, ...casesByStatus.exchanged, ...casesByStatus.completed]
                    .sort((a, b) => {
                      // Sort by status priority then by fee amount
                      const statusOrder = { active: 1, exchanged: 2, completed: 3 };
                      if (statusOrder[a.status] !== statusOrder[b.status]) {
                        return statusOrder[a.status] - statusOrder[b.status];
                      }
                      return (b.agreed_fee || 0) - (a.agreed_fee || 0);
                    })
                    .map((caseData) => (
                      <div
                        key={caseData.case_id}
                        onClick={() => navigate(`/sales/case/${caseData.case_id}/hub`)}
                        className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-md hover:border-blue-300 cursor-pointer transition-all group"
                        data-testid={`property-row-${caseData.case_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[caseData.status]?.color}`} />
                          <div>
                            <p className="font-medium text-slate-800 group-hover:text-blue-600">
                              {caseData.address_line1}
                            </p>
                            <p className="text-sm text-slate-500">{caseData.town} {caseData.postcode}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge className={`${STATUS_CONFIG[caseData.status]?.bgLight} ${STATUS_CONFIG[caseData.status]?.textColor}`}>
                            {STATUS_CONFIG[caseData.status]?.label}
                          </Badge>
                          {caseData.agreed_fee > 0 && (
                            <p className="font-semibold text-emerald-600 min-w-[80px] text-right">
                              £{caseData.agreed_fee?.toLocaleString()}
                            </p>
                          )}
                          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-500" />
                        </div>
                      </div>
                    ))}
                  
                  {/* Empty state */}
                  {[...casesByStatus.active, ...casesByStatus.exchanged, ...casesByStatus.completed].length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Home className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No properties in progress</p>
                    </div>
                  )}
                </div>

                {/* Summary Footer */}
                <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-500">SSTC</p>
                    <p className="text-xl font-bold text-blue-600">{casesByStatus.active.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Exchanged</p>
                    <p className="text-xl font-bold text-purple-600">{casesByStatus.exchanged.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completed</p>
                    <p className="text-xl font-bold text-green-600">{casesByStatus.completed.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Predictions View */}
          <TabsContent value="predictions">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Completion Predictions
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Assign SSTC cases to months when you expect them to complete
                </p>
              </CardHeader>
              <CardContent>
                {/* Monthly Columns */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {monthlyPredictions.map((month) => (
                    <div 
                      key={month.key}
                      className="border-2 border-dashed border-slate-200 rounded-lg p-3 min-h-[200px] bg-slate-50/50"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const caseId = e.dataTransfer.getData("caseId");
                        if (caseId) savePrediction(caseId, month.key);
                      }}
                    >
                      <div className="text-center mb-3 pb-2 border-b">
                        <p className="font-semibold text-slate-700">{month.short}</p>
                        <p className="text-lg font-bold text-emerald-600">
                          £{month.totalFees.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">{month.cases.length} cases</p>
                      </div>
                      
                      <div className="space-y-2">
                        {month.cases.map(c => (
                          <div 
                            key={c.case_id}
                            className="p-2 bg-white rounded border text-xs group relative"
                          >
                            <button
                              onClick={() => removePrediction(c.case_id)}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <p className="font-medium truncate">{c.address_line1}</p>
                            <p className="text-emerald-600">£{c.agreed_fee?.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary Bar */}
                <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Predicted Fees (Next 6 Months)</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        £{monthlyPredictions.reduce((sum, m) => sum + m.totalFees, 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Cases Assigned</p>
                      <p className="text-2xl font-bold">
                        {Object.keys(predictions).length} / {casesByStatus.active.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Unassigned SSTC Cases */}
                <div className="mt-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <GripVertical className="h-4 w-4" />
                    Drag SSTC cases to assign predictions
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {casesByStatus.active
                      .filter(c => !predictions[c.case_id])
                      .map(c => (
                        <div
                          key={c.case_id}
                          className="p-2 bg-white border rounded cursor-grab hover:shadow-md transition-shadow"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("caseId", c.case_id);
                          }}
                        >
                          <p className="font-medium text-sm truncate">{c.address_line1}</p>
                          <p className="text-xs text-emerald-600">£{c.agreed_fee?.toLocaleString()}</p>
                        </div>
                      ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Prediction Modal */}
      <Dialog open={showPredictionModal} onOpenChange={setShowPredictionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Completion Prediction</DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <div className="py-4">
              <p className="font-medium">{selectedCase.address_line1}</p>
              <p className="text-sm text-slate-500">{selectedCase.town}</p>
              <p className="text-emerald-600 font-semibold">£{selectedCase.agreed_fee?.toLocaleString()}</p>
              
              <div className="mt-4">
                <label className="text-sm font-medium">Expected Completion Month</label>
                <Select
                  value={predictions[selectedCase.case_id] || ""}
                  onValueChange={(value) => savePrediction(selectedCase.case_id, value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select month..." />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fee List Modal */}
      <Dialog open={showFeeListModal} onOpenChange={setShowFeeListModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              {STATUS_CONFIG[feeListStatus]?.label || feeListStatus} - Fee Breakdown
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {feeListStatus && casesByStatus[feeListStatus]?.length > 0 ? (
              <div className="space-y-2">
                {casesByStatus[feeListStatus].map(c => (
                  <div 
                    key={c.case_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => { setShowFeeListModal(false); navigate(`/sales/case/${c.case_id}`); }}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{c.address_line1}</p>
                      <p className="text-sm text-slate-500">{c.town}, {c.postcode}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">
                        £{(c.agreed_fee || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="font-semibold">Total Fees</span>
                  <span className="text-xl font-bold text-emerald-600">
                    £{casesByStatus[feeListStatus].reduce((sum, c) => sum + (c.agreed_fee || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No cases in this status</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
